import type { VariantResponse } from 'models/api';
import type { AssetResponse } from 'models/asset';
import type { Product } from 'models/product';
import { filterTruthy, withTruthyProps } from 'utils/collection';
import { is } from 'utils/helpers';

type ProductOrImages =
	| { images?: AssetResponse[] }
	| AssetResponse[]
	| undefined;

/**
 * Returns the image for the productCard.
 */
export function getProductImage(productOrImages: ProductOrImages) {
	const formats = is.array(productOrImages)
		? productOrImages
		: productOrImages?.images;

	return withTruthyProps(
		formats?.find((img) => img.formatName === 'Templated square transparent'),
		'src',
	);
}

/**
 * Returns the main image for the product.
 */
export function getMainImageDetail(
	product: Product | VariantResponse | undefined,
) {
	return withTruthyProps(
		product?.images?.find(
			(img) =>
				img.subType === 'MainProductImage' &&
				img.formatName === 'Templated square transparent',
		),
		'src',
	);
}

/**
 * Returns a sorted array of all product images.
 */
export function getAllImages(product: Product | VariantResponse | undefined) {
	return filterTruthy(
		product?.images?.filter(
			(img) =>
				img.type === 'ProductImage' &&
				img.formatName === 'Templated square transparent',
		),
		'src',
	);
}

/**
 * Returns the brand logo image for the product.
 */
export function getBrandLogoImage(
	product: Product | VariantResponse | undefined,
) {
	return withTruthyProps(
		product?.brand?.images?.find(
			(img) =>
				img.subType === 'PrimaryLogo' &&
				img.formatName === 'ODL 80px height transparent',
		),
		'src',
	);
}

export function getVerticalEnergyLabel(
	product: Product | VariantResponse | undefined,
) {
	return withTruthyProps(
		product?.energyInformationResponse?.labels?.find(
			(img) => img.formatName === 'ODL 360px width transparent',
		),
		'src',
	);
}

export function getEnergySymbol(
	product: Product | VariantResponse | undefined,
) {
	const symbols = product?.energyInformationResponse?.symbols;
	const symbol =
		symbols?.find((img) => img.subType === 'EfficiencySVGSymbol') ??
		symbols?.find((img) => img.subType === 'EfficiencySymbol');
	return withTruthyProps(symbol, 'src');
}

/**
 * Returns the configurator grid image for the product.
 */
export function getConfiguratorGridImage(
	product: Product | VariantResponse | undefined,
) {
	return product?.images?.find(
		(image) =>
			image.subType === 'ConfiguratorProductImage' &&
			image.formatName === 'Templated square transparent',
	);
}
